import * as Dialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode, useEffect, useRef } from 'react';
import XIcon from '@/components/icons/XIcon';
import { ButtonStack } from '@/ui-kit/components/ButtonStack/ButtonStack';
import { cn } from '@/ui-kit/utils';

const transition = {
  type: 'tween',
  duration: 0.4,
} as const;

type ModalRootProps = {
  isOpen: boolean;
  children?: ReactNode;
  onDismiss?: () => void;
};

export const ModalRoot = ({ isOpen, children, onDismiss }: ModalRootProps) => (
  <Dialog.Root
    open={isOpen}
    onOpenChange={open => {
      if (!open) {
        onDismiss?.();
      }
    }}
  >
    <AnimatePresence>{!!isOpen && children}</AnimatePresence>
  </Dialog.Root>
);

type ModalProps = {
  topLeftContent?: ReactNode;
  closeButtonAriaLabel?: string;
  title: ReactNode;
  className?: string;
  children: ReactNode;
};

export const Modal = ({
  title,
  topLeftContent,
  closeButtonAriaLabel = 'Close',
  className,
  children,
}: ModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (!contentRef.current) {
        return;
      }

      const { height } = contentRef.current.getBoundingClientRect();
      contentRef.current.style.setProperty(
        '--modal-border-radius',
        height >= window.innerHeight ? '0' : '16px',
      );
    });

    resizeObserver.observe(window.document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Dialog.Portal forceMount>
      <motion.div
        className={cn('fixed inset-0 z-9999 flex items-end justify-center lg:items-center', className)}
        initial="hidden"
        animate="visible"
        exit="hidden"
        transition={transition}
      >
        <Dialog.Overlay asChild forceMount>
          <motion.div
            className="bg-background-overlay fixed inset-0"
            variants={{
              visible: { opacity: 1 },
              hidden: { opacity: 0 },
            }}
            transition={transition}
          />
        </Dialog.Overlay>

        <Dialog.Content asChild forceMount>
          <motion.div
            ref={contentRef}
            className={cn(
              'bg-surface-default z-1 max-h-full w-full overflow-auto',
              'rounded-t-(--modal-border-radius) lg:rounded-(--modal-border-radius)',
              'lg:max-h-(--modal-desktop-size) lg:w-(--modal-desktop-size) lg:max-w-[calc(100vw-48px)]',
              '[--modal-border-radius:16px] [--modal-desktop-size:640px] [--modal-horizontal-padding:24px] [--modal-spacing:16px] [--modal-vertical-padding:16px]',
              'lg:[--modal-horizontal-padding:48px] lg:[--modal-spacing:24px] lg:[--modal-vertical-padding:48px]',
            )}
            variants={{
              visible: { y: '0%' },
              hidden: { y: '100%' },
            }}
            transition={transition}
          >
            <header className="bg-surface-default sticky top-0 flex flex-col gap-(--modal-spacing) mask-[linear-gradient(to_top,transparent,black_8%)] pt-(--modal-vertical-padding) pr-(--modal-horizontal-padding) pb-(--modal-spacing) pl-(--modal-horizontal-padding)">
              <div className="flex h-8 items-center justify-between">
                <div className="flex h-8 flex-1 items-center">{topLeftContent}</div>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="-mr-2 flex h-8 w-8 cursor-pointer items-center justify-center border-none bg-transparent p-0"
                    aria-label={closeButtonAriaLabel}
                  >
                    <XIcon className="text-icon-default" aria-hidden="true" />
                  </button>
                </Dialog.Close>
              </div>

              <Dialog.Title asChild>
                <h2 className="text-heading m-0 lg:text-center">{title}</h2>
              </Dialog.Title>
            </header>

            <Dialog.Description asChild>
              <div className="px-(--modal-horizontal-padding) pb-(--modal-vertical-padding)">{children}</div>
            </Dialog.Description>
          </motion.div>
        </Dialog.Content>
      </motion.div>
    </Dialog.Portal>
  );
};

type ModalActionsProps = {
  children: ReactNode;
  className?: string;
};

export const ModalActions = ({ children, className }: ModalActionsProps) => (
  <ButtonStack className={cn('mt-(--modal-spacing) items-center', className)}>{children}</ButtonStack>
);
